import { useEffect, useState } from "react";
import KilleenHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const faqs = [
  { q: "How fast can I get a passport in Killeen?", a: "We offer Killeen-area clients expedited processing as fast as 24 hours. This is critical for Fort Cavazos military personnel who receive short-notice deployment orders. Standard expedited service typically takes 5–10 business days. The exact timeline depends on your application type, document completeness, and current State Department volumes." },
  { q: "Can military personnel at Fort Cavazos get emergency passports?", a: "Yes. Military members with official deployment orders requiring a personal (tourist) passport — not a no-fee official passport — can use our expedited service. Orders with travel within 14 days qualify for our emergency tier. We understand the specific documentation Fort Cavazos personnel need and streamline the process accordingly." },
  { q: "Where is the nearest passport acceptance facility to Killeen?", a: "Killeen has several acceptance facilities including the main Killeen Post Office, Harker Heights Post Office, and the Bell County Clerk's office in Belton. For faster appointment availability, Temple Post Office is 20 minutes south. We provide a complete facility list with current appointment availability." },
  { q: "Does Fort Cavazos have an on-base passport acceptance facility?", a: "Yes. Fort Cavazos (formerly Fort Hood) has a designated passport acceptance facility on post, which serves active-duty personnel, dependents, and DoD civilians. Hours and appointment availability vary — check ACS or the Killeen Main Gate services. Our service handles courier submission regardless of which acceptance facility you use." },
  { q: "How much does expedited passport service cost for Killeen residents?", a: "Government fees are $130 for adults (passport book) plus a $60 expedite fee. Our courier service adds a processing fee covering document review, overnight shipping, and expedited submission. Our rates are typically 30–100% lower than FedEx, Staples, and other resellers. We offer a best price guarantee." },
  { q: "What if I'm a Fort Cavazos dependent who needs a passport?", a: "Fort Cavazos military dependents apply exactly like any civilian — through an acceptance facility, using Form DS-11 for new passports or DS-82 for renewals. The on-post acceptance facility or Killeen/Harker Heights post offices are the most convenient. We handle the expedited submission through our State Department courier channel after your application is accepted." },
  { q: "Can I use a Killeen passport for Mexico travel?", a: "Yes. A U.S. passport book or passport card works for Mexico. Passport cards are a lower-cost option for land and sea travel to Mexico and Canada. If you fly to Mexico from Killeen (via Austin-Bergstrom), you need a passport book. We can process passport card applications as well as books." },
  { q: "How far is Killeen from the nearest passport agency?", a: "The Houston Passport Agency is approximately 190 miles southeast of Killeen (about 3 hours). The Dallas/Fort Worth area is 140 miles north. For time-critical cases, our courier service eliminates the need for you to travel to an agency — we handle the expedited submission on your behalf." },
  { q: "Can Central Texas College students get expedited passports for study abroad?", a: "Absolutely. CTC and Texas A&M Central Texas students often need passports for study abroad programs with firm departure dates. We process student study-abroad passport requests regularly and understand academic program timelines. Start at least 3 weeks before your departure date for comfort; contact us if you're closer than that." },
  { q: "What if my passport is lost or stolen while stationed at Fort Cavazos?", a: "File a police report (MP report if on-base), complete Form DS-64 (Statement Regarding Lost or Stolen Passport), then apply for a replacement using DS-11. Visit the on-post acceptance facility or Killeen Post Office in person. With upcoming travel or deployment, our expedited service can process the replacement in as few as 24 hours." },
];

function KilleenStickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = ["processing", "services", "local-area", "travel-timing", "destinations", "testimonials", "facilities", "comparison", "mistakes", "how-it-works", "faq"];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 120 && rect.bottom >= 120) { setActive(id); break; }
        }
      }
    };
    window.addEventListener("scroll", handle);
    handle();
    return () => window.removeEventListener("scroll", handle);
  }, []);
  const links = [
    { id: "processing", label: "Processing" }, { id: "services", label: "Services" }, { id: "local-area", label: "Local Area" },
    { id: "travel-timing", label: "Travel" }, { id: "destinations", label: "Destinations" }, { id: "testimonials", label: "Reviews" },
    { id: "facilities", label: "Facilities" }, { id: "comparison", label: "Compare" }, { id: "mistakes", label: "Mistakes" },
    { id: "how-it-works", label: "How It Works" }, { id: "faq", label: "FAQ" },
  ];
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 overflow-x-auto">
      <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center gap-1 py-2">
        {links.map((l) => (
          <a key={l.id} href={`#${l.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${active === l.id ? "bg-[#1A365D] text-white" : "text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D]"}`}>{l.label}</a>
        ))}
      </div>
    </div>
  );
}

function KilleenProcessingSection() {
  return (
    <section id="processing" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Killeen Passport Processing Times</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Multiple speed tiers for Fort Cavazos military and Killeen-area civilians. Choose the timeline that fits your urgency.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $499", badge: "Fastest", desc: "For Killeen residents and Fort Cavazos personnel with imminent departure. Includes same-day document review and overnight courier submission." },
            { title: "Rush", time: "3–5 Business Days", price: "From $299", badge: "Popular", desc: "For military dependents and Killeen families with travel in 1–2 weeks. Balanced speed and value with full document review." },
            { title: "Standard Expedited", time: "7–10 Business Days", price: "From $149", badge: "Best Value", desc: "For Killeen residents planning ahead. Still faster than standard post office processing with professional document handling." },
          ].map((p, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all">
              <div className="flex items-center justify-between mb-4">
                <span className="bg-[#1A365D] text-white text-xs font-bold px-2.5 py-1 rounded-full">{p.badge}</span>
                <span className="text-[#F6C344] font-bold text-sm">{p.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{p.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{p.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KilleenServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "Emergency Passport", desc: "24–48 hour processing for Fort Cavazos personnel and Killeen residents with imminent departure." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for Killeen residents with passports issued within 15 years at age 16+." },
    { icon: "ri-add-circle-line", title: "New Passport", desc: "First-time applications for Killeen adults, military dependents, and CTC students." },
    { icon: "ri-shield-star-line", title: "Military Deployment", desc: "Expedited personal passport for Fort Cavazos soldiers needing civilian travel documents for deployment." },
    { icon: "ri-parent-line", title: "Child Passport", desc: "Minors under 16 with parental consent guidance for Killeen and Harker Heights families." },
    { icon: "ri-error-warning-line", title: "Lost or Stolen", desc: "Replacement service with DS-64 form guidance for Killeen residents and military families." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Passport correction after marriage, divorce, or legal name changes in Texas." },
    { icon: "ri-shield-check-line", title: "Document Review", desc: "Pre-submission review catches errors that delay Killeen passport applications." },
  ];
  return (
    <section id="services" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Killeen Passport Services</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">Comprehensive passport solutions for Fort Cavazos military families, veterans, and Central Texas civilians.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {services.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${s.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KilleenLocalAreaSection() {
  return (
    <section id="local-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services Tailored for Killeen &amp; Fort Cavazos</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Killeen is the gateway city to Fort Cavazos, one of the largest military installations in the world. With nearly 45,000 active-duty soldiers, plus tens of thousands of military family members, civilian employees, and contractors, the greater Killeen-Temple-Fort Hood area generates passport demand unlike any comparably sized Texas city. Soldiers, spouses, and children navigate passport requirements constantly — from PCS moves to allied countries to personal vacation travel to recreational travel during leave periods.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Fort Cavazos soldiers often require passports on very short timelines. A deployment order to a NATO partner nation, a temporary duty assignment requiring travel through civilian airports in Europe, or a family emergency that requires a soldier's spouse to fly internationally — all create urgent passport situations. We process dozens of Fort Cavazos-area expedited passport requests monthly, and we understand the unique documentation that military applications involve.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Beyond the military community, Killeen is home to Central Texas College and Texas A&M Central Texas, both of which send students abroad through study-abroad programs, service-learning trips, and academic exchanges. A Baylor University student driving in from Waco for a passport acceptance facility appointment, or a CTC student applying for the first time ahead of a European semester, represents a growing share of Killeen passport demand.
          </p>
          <p className="text-gray-600 text-base leading-relaxed mb-4">
            Killeen's Hispanic community also maintains strong family ties to Mexico, with many residents traveling to see family in Monterrey, Mexico City, Guadalajara, and smaller hometowns throughout Tamaulipas and Nuevo León. These family heritage visits — holiday travel, quinceañeras, medical tourism — create recurring expedited passport demand, especially around Christmas, Easter, and summer vacation windows.
          </p>
          <p className="text-gray-600 text-base leading-relaxed">
            One often-overlooked aspect of Killeen passport demand is the Department of Defense civilian workforce. Thousands of DoD civilians working on and around Fort Cavazos travel internationally for official business — and those trips sometimes require personal passports rather than official no-fee passports. These government contractors and civilian employees represent a significant share of our Killeen expedited service volume, particularly for travel to Europe and East Asia.
          </p>
        </div>
      </div>
    </section>
  );
}

function KilleenTravelTimingSection() {
  return (
    <section id="travel-timing" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Travel &amp; Passport Timing for Killeen Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {[
            {
              icon: "ri-shield-star-line",
              title: "Fort Cavazos Deployment Orders",
              sub: "Short-notice military travel with civilian passports",
              body: "Fort Cavazos soldiers sometimes receive orders requiring a personal (non-official) passport for travel through civilian airports or to allied nations. Orders can arrive with 7–14 days' notice. Our emergency tier processes applications in 24–48 hours, which is compatible with even the tightest deployment windows. We also assist military family members who need to travel separately from their service member.",
              tip: "Military tip: A no-fee official passport is issued separately through military channels for official duty travel. If your orders require a personal passport, contact us immediately — timeline is everything."
            },
            {
              icon: "ri-flight-takeoff-line",
              title: "Austin-Bergstrom International Airport",
              sub: "75 miles south of Killeen, the primary international gateway",
              body: "Killeen residents flying internationally depart primarily through Austin-Bergstrom (AUS), which offers direct international flights to Cancun, London, Mexico City, and Central America on American, United, Delta, and Southwest. The 75-mile drive from Killeen to AUS is manageable, but last-minute passport discovery at booking still puts you in an urgent situation. For Killeen, we recommend verifying passport validity the moment you start planning any international trip.",
              tip: "Airport tip: AUS is 75 miles from Killeen — about 1 hour 15 minutes. Some Killeen travelers also route through Dallas-Fort Worth (2 hours) for more international connections."
            },
            {
              icon: "ri-heart-pulse-line",
              title: "Mexico Family & Medical Travel",
              sub: "Cross-border travel to Monterrey, Tamaulipas, and beyond",
              body: "Killeen's large Hispanic community — many with family roots in Nuevo León, Tamaulipas, and Coahuila — regularly crosses into Mexico for holiday visits, medical treatment, dental care, and family events like quinceañeras and weddings. These trips often have fixed dates tied to family celebrations or medical appointments. First-time passport applicants needing documents for a planned family trip to Monterrey often discover the 10–13 week standard timeline far too late.",
              tip: "Mexico travel tip: A passport card works for land crossings into Mexico and is cheaper than a book. If you fly to Mexico, you need a passport book. We process both."
            },
            {
              icon: "ri-graduation-cap-line",
              title: "CTC & TAMU-CT Study Abroad",
              sub: "Academic departure deadlines with no flexibility",
              body: "Central Texas College and Texas A&M Central Texas both offer study abroad programs with fixed program-start dates. Students who procrastinate on passport applications — or who apply and discover their application has an error — face losing their program slot. We work with Killeen-area students applying for the first time and those who need renewals, and we help identify document errors before submission to avoid delays.",
              tip: "Student tip: Study abroad programs typically require your passport to be valid for 6 months beyond your return date. Check your expiration date before the program deadline, not the week before departure."
            },
          ].map((card, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                  <i className={`${card.icon} text-[#1A365D] text-lg`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#1A365D] text-sm">{card.title}</h3>
                  <p className="text-gray-400 text-xs">{card.sub}</p>
                </div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-4">{card.body}</p>
              <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
                <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-600 text-xs leading-relaxed"><strong className="text-[#1A365D]">Tip:</strong> {card.tip}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-shield-star-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Killeen's 190-Mile Gap to the Houston Passport Agency</h3>
            <p className="text-white/60 text-sm leading-relaxed">The nearest passport agency is in Houston — about 190 miles away (nearly 3 hours). For Fort Cavazos personnel on restricted leave or Killeen residents without the ability to make a 6-hour round trip, our remote courier service eliminates that burden entirely. We submit your application on your behalf, no agency visit required.</p>
          </div>
          <a href="https://fastpassportcenter.com/checkout/" className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0">Get Started <i className="ri-arrow-right-line ml-1"></i></a>
        </div>
      </div>
    </section>
  );
}

function KilleenDestinationsSection() {
  const destinations = [
    { name: "Mexico", context: "Family visits to Monterrey, Nuevo León, medical tourism, holiday travel for Killeen's Hispanic community", urgency: "Fixed family event dates mean little flexibility on passport timing", icon: "ri-map-pin-2-line" },
    { name: "Germany & NATO Europe", context: "Fort Cavazos soldiers traveling to Grafenwöhr, Ramstein, and other NATO partner facilities as civilians", urgency: "Deployment-adjacent travel often requires civilian passports with 7–14 days notice", icon: "ri-map-pin-2-line" },
    { name: "South Korea", context: "DoD civilians and contractors traveling to Camp Humphreys and Korean Peninsula installations", urgency: "Government civilian assignments typically booked 2–4 weeks out", icon: "ri-map-pin-2-line" },
    { name: "Caribbean & Cancun", context: "Military families using leave periods for vacations, PCS celebration trips, anniversary travel", urgency: "Leave periods are fixed — missed travel due to passport issues cannot be rescheduled", icon: "ri-map-pin-2-line" },
    { name: "Italy & Southern Europe", context: "Study abroad from CTC and TAMU-CT, military rest-and-recreation travel, cultural heritage trips", urgency: "Program start dates are immovable — late passport applications mean losing your spot", icon: "ri-map-pin-2-line" },
    { name: "Honduras & El Salvador", context: "Heritage travel for Central American families in Killeen area, mission trips from local churches", urgency: "Mission trip group departures have fixed dates with no solo rebooking option", icon: "ri-map-pin-2-line" },
  ];
  const stats = [
    { stat: "45,000+", label: "Active-duty soldiers at Fort Cavazos generating regular passport demand" },
    { stat: "7–14 Days", label: "Typical notice window for Fort Cavazos military passport situations" },
    { stat: "190 Miles", label: "Distance from Killeen to the nearest passport agency in Houston" },
    { stat: "24 Hours", label: "Fastest turnaround we offer for emergency Killeen passport requests" },
  ];
  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Travel Intelligence</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Killeen Travelers Go — And Why Passports Get Urgent</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
          {stats.map((s, i) => (
            <div key={i} className="bg-[#1A365D] rounded-lg p-6 text-center">
              <p className="font-serif text-3xl font-bold text-[#F6C344] mb-2">{s.stat}</p>
              <p className="text-white/60 text-xs leading-relaxed">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {destinations.map((d, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all group">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 group-hover:bg-[#F6C344]/15 transition-colors flex-shrink-0">
                  <i className={`${d.icon} text-[#1A365D] group-hover:text-[#F6C344] transition-colors text-lg`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{d.name}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{d.context}</p>
              <div className="flex items-start gap-2">
                <i className="ri-time-line text-[#F6C344] text-xs flex-shrink-0 mt-0.5"></i>
                <p className="text-gray-400 text-xs leading-relaxed italic">{d.urgency}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
              <i className="ri-links-line text-[#1A365D] text-xl"></i>
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-[#1A365D] text-sm mb-1">Explore Other Texas Service Areas</h3>
              <p className="text-gray-500 text-sm leading-relaxed">We serve travelers across Texas with the same expedited passport processing expertise.</p>
            </div>
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <a href="/cities/austin" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Austin <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/waco" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Waco <i className="ri-arrow-right-line text-xs"></i></a>
              <a href="/cities/houston" className="inline-flex items-center gap-1.5 bg-white hover:bg-[#1A365D] text-[#1A365D] hover:text-white border border-gray-200 hover:border-[#1A365D] font-medium text-xs px-3.5 py-2 rounded-md transition-all whitespace-nowrap">Houston <i className="ri-arrow-right-line text-xs"></i></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function KilleenTestimonialsSection() {
  const testimonials = [
    { name: "SSG Ramon V.", location: "Fort Cavazos", text: "Got orders to travel through Germany on leave. Needed a personal passport and had 9 days. Fast Passport Center got it done in 48 hours. Nobody else could touch that timeline.", rating: 5 },
    { name: "Maria G.", location: "Harker Heights", text: "My mother had a medical emergency in Monterrey. I needed a passport renewal in 3 days. They reviewed my documents, flagged an issue with my photo, and still got it back in time. Incredible service.", rating: 5 },
    { name: "The Williams Family", location: "Killeen", text: "PCS celebration trip to Cancun and our daughter's passport was expired. They walked us through everything — the acceptance facility, the forms, the photo. Got it back in 5 business days. Vacation saved.", rating: 5 },
    { name: "DoD Contractor, Kevin B.", location: "Fort Cavazos Area", text: "I travel to Korea and Japan for work. Used Fast Passport Center twice for renewals. Both times fast, both times accurate. The document review caught a name discrepancy the first time. Well worth it.", rating: 5 },
  ];
  return (
    <section id="testimonials" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Killeen Clients Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white rounded-lg p-7 border border-gray-200">
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: t.rating }).map((_, j) => (<i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>))}
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">&quot;{t.text}&quot;</p>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-sm">{t.name[0]}</div>
                <div>
                  <p className="font-semibold text-[#1A365D] text-sm">{t.name}</p>
                  <p className="text-gray-400 text-xs">{t.location}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KilleenFacilitiesSection() {
  const facilities = [
    { name: "Killeen Main Post Office", address: "802 N. 2nd St, Killeen, TX 76541", phone: "(254) 526-5601", photo: "Yes", appointment: "Required" },
    { name: "Harker Heights Post Office", address: "307 Millers Crossing, Harker Heights, TX 76548", phone: "(254) 699-3481", photo: "Yes", appointment: "Walk-in" },
    { name: "Fort Cavazos ACS Passport Office", address: "Fort Cavazos, TX 76544 (on-post)", phone: "Call ACS", photo: "Varies", appointment: "Required" },
    { name: "Bell County Clerk — Belton", address: "550 E 2nd Ave, Belton, TX 76513", phone: "(254) 933-5160", photo: "No", appointment: "Walk-in" },
    { name: "Temple Post Office", address: "2 N 3rd St, Temple, TX 76501", phone: "(254) 778-3781", photo: "Yes", appointment: "Required" },
    { name: "Copperas Cove Post Office", address: "301 Ave D, Copperas Cove, TX 76522", phone: "(254) 547-2271", photo: "Yes", appointment: "Walk-in" },
  ];
  return (
    <section id="facilities" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Killeen &amp; Fort Cavazos Passport Acceptance Facilities</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">First-time applicants and minors must visit an acceptance facility in person. Here are area options with contact information.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#1A365D]">
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Facility</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Address</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Phone</th>
                <th className="text-white font-semibold text-sm px-4 py-3">Photo</th>
                <th className="text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Appointment</th>
              </tr>
            </thead>
            <tbody>
              {facilities.map((f, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-[#F7F8FA] transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{f.name}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.address}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.phone}</td>
                  <td className="px-4 py-3.5 text-sm">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${f.photo === "Yes" ? "bg-green-50 text-green-600" : "bg-gray-100 text-gray-500"}`}>
                      <i className={f.photo === "Yes" ? "ri-check-line" : "ri-close-line"}></i>{f.photo}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{f.appointment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 bg-[#F6C344]/8 rounded-lg p-4 flex items-start gap-3">
          <i className="ri-information-line text-[#F6C344] text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-gray-600 text-sm leading-relaxed">Hours and appointment policies change frequently. Fort Cavazos on-post access requires a valid ID or visitor pass. We recommend calling ahead to confirm availability and access requirements.</p>
        </div>
      </div>
    </section>
  );
}

function KilleenComparisonSection() {
  return (
    <section id="comparison" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Killeen Passport Service Comparison</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tl-lg">Feature</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Fast Passport Center</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3">Post Office</th>
                <th className="bg-[#1A365D] text-white font-semibold text-sm px-4 py-3 rounded-tr-lg">Third-Party Resellers</th>
              </tr>
            </thead>
            <tbody>
              {[
                { feature: "Fastest Processing", us: "24–48 hours", post: "10–13 weeks", reseller: "7–14 days" },
                { feature: "Document Review", us: "Included", post: "None", reseller: "Minimal or extra fee" },
                { feature: "Military Familiarity", us: "Experienced with Fort Cavazos", post: "Generic process", reseller: "Generic process" },
                { feature: "Price Guarantee", us: "Best price guarantee", post: "Government fees only", reseller: "Often 30–100% markup" },
                { feature: "Courier Status", us: "Registered State Dept. courier", post: "Government direct", reseller: "Reseller, not registered" },
                { feature: "Phone Support", us: "Yes, real agents", post: "1-877 line only", reseller: "Often limited" },
                { feature: "Shipping Included", us: "Overnight both ways", post: "You pay shipping", reseller: "Varies by plan" },
                { feature: "BBB Rating", us: "A+", post: "N/A", reseller: "Varies" },
                { feature: "Same-Day Submission", us: "Yes, for emergency tier", post: "No", reseller: "Rarely" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-gray-100 hover:bg-white transition-colors">
                  <td className="px-4 py-3.5 text-sm font-medium text-[#1A365D]">{row.feature}</td>
                  <td className="px-4 py-3.5 text-sm text-green-600 font-medium">{row.us}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.post}</td>
                  <td className="px-4 py-3.5 text-sm text-gray-500">{row.reseller}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function KilleenMistakesSection() {
  const mistakes = [
    { title: "Confusing Official vs. Personal Passports", desc: "Military members are often issued no-fee official passports for official travel. For personal leave travel, you need a standard personal passport. Many Fort Cavazos soldiers confuse these and apply for the wrong type, causing delays." },
    { title: "Waiting for On-Post Processing", desc: "The Fort Cavazos passport office has limited appointment availability. During high-demand periods (summer PCS season, holiday leave), waiting for an on-post appointment means weeks of delay. Off-post facilities often have earlier slots." },
    { title: "Using the Wrong Form", desc: "DS-11 for new passports and children, DS-82 for renewals. Using the wrong form results in rejection at the acceptance facility, adding days or weeks to your timeline." },
    { title: "Forgetting Parental Consent for Minors", desc: "Military families with single-parent custody arrangements, or where one parent is deployed, must provide a DS-3053 notarized consent or custody documentation. Missing this form causes immediate rejection." },
    { title: "Not Accounting for Austin Agency Distance", desc: "The nearest passport agency is in Houston (190 miles). Killeen residents cannot easily visit an agency for same-day service. Planning ahead with our courier service avoids this geographic disadvantage entirely." },
  ];
  return (
    <section id="mistakes" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Killeen Passport Mistakes</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50 flex-shrink-0">
                  <i className="ri-error-warning-line text-red-400 text-sm"></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{m.title}</h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KilleenHowItWorksSection() {
  const steps = [
    { num: "01", title: "Start Online", desc: "Complete our intake form and upload your documents. We review everything within 4 hours during business hours." },
    { num: "02", title: "Visit Acceptance Facility", desc: "For new applications and minors, visit a Killeen-area acceptance facility — on-post, post office, or Bell County Clerk." },
    { num: "03", title: "Ship to Us", desc: "Send your sealed application via overnight courier. We include a prepaid shipping label." },
    { num: "04", title: "Expedited Submission", desc: "We submit through our registered State Department courier channel. You receive tracking at every stage." },
    { num: "05", title: "Receive Your Passport", desc: "Your passport is delivered to your Killeen address via secure overnight shipping within your chosen timeline." },
  ];
  return (
    <section id="how-it-works" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Process</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Killeen Residents</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="bg-white rounded-lg p-6 border border-gray-200 text-center">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-sm mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KilleenFAQSection() {
  return (
    <section id="faq" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Killeen Passport Questions</h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm mb-3">{faq.q}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function KilleenResourcesSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Killeen Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { href: "https://travel.state.gov/content/travel/en/passports.html", icon: "ri-government-line", title: "U.S. State Department", desc: "Official passport information, forms, and processing status updates." },
            { href: "https://travel.state.gov/content/travel/en/traveladvisories/traveladvisories.html", icon: "ri-earth-line", title: "Travel Advisories", desc: "Current safety and entry requirements for every country." },
            { href: "https://www.cbp.gov/travel/travel-industry-persons/visa-waiver-program", icon: "ri-visa-line", title: "Visa Waiver Program", desc: "Check if your destination participates in the ESTA visa waiver program." },
          ].map((r, i) => (
            <a key={i} href={r.href} target="_blank" rel="noopener noreferrer" className="bg-white rounded-lg p-6 border border-gray-200 hover:border-[#F6C344] transition-all">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 mb-4">
                <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
              </div>
              <h3 className="font-bold text-[#1A365D] text-sm mb-2">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{r.desc}</p>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function KilleenFinalCTA() {
  return (
    <section className="bg-[#1A365D] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-4">Ready to Get Your Killeen Passport Fast?</h2>
        <p className="text-white/70 text-lg leading-relaxed mb-8">Whether you are a Fort Cavazos soldier with orders, a military family planning leave travel, or a Killeen resident with a trip approaching, we will get your passport processed on time.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">
          Start My Passport <i className="ri-arrow-right-line"></i>
        </a>
        <p className="text-white/40 text-sm mt-6">A+ BBB rated. 20+ years experience. Registered State Department courier.</p>
      </div>
    </section>
  );
}

export default function KilleenPage() {
  useEffect(() => {
    document.title = "Emergency Passport Killeen TX — Expedited Service Near Fort Cavazos | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    const pageUrl = "https://fastpassportcenter.com/killeen/";
    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Killeen TX, serving Fort Cavazos military, dependents, and Bell County residents. As fast as 24 hours. A+ BBB rated registered State Dept. courier. Best price guarantee.");
    setMeta("geo.placename", "Killeen, Texas");
    setMeta("geo.region", "US-TX");
    setMeta("geo.position", "31.1171;-97.7278");
    setMeta("ICBM", "31.1171, -97.7278");
    const graph = [
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
        { "@type": "ListItem", position: 2, name: "Texas", item: "https://fastpassportcenter.com/texas/" },
        { "@type": "ListItem", position: 3, name: "Killeen Passport Services", item: pageUrl },
      ]},
      { "@type": "FAQPage", mainEntity: faqs.map((faq) => ({ "@type": "Question", name: faq.q, acceptedAnswer: { "@type": "Answer", text: faq.a } })) },
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data-killeen";
    document.getElementById("structured-data-killeen")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);
    return () => { document.getElementById("structured-data-killeen")?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <KilleenHeroSection />
      <KilleenStickyNav />
      <KilleenProcessingSection />
      <KilleenServicesSection />
      <KilleenLocalAreaSection />
      <KilleenTravelTimingSection />
      <KilleenDestinationsSection />
      <KilleenTestimonialsSection />
      <KilleenFacilitiesSection />
      <KilleenComparisonSection />
      <KilleenMistakesSection />
      <KilleenHowItWorksSection />
      <KilleenFAQSection />
      <KilleenResourcesSection />
      <SEOCrossLinks currentRoute="/cities/killeen" state="TX" />
      <KilleenFinalCTA />
    </div>
  );
}